import { IKey } from '@/common/constants/ikey'
import { extractUniDataTableRows } from '@/common/utils/extractUniDataTableRows'
import {
  buildCheckboxTableGetDataField,
  isSameTableSelectionRow,
} from '@/common/utils/orderCheckboxSelectionRowsByDisplay'
import { getFieldValueFromRow } from '@/common/utils/treeTableRowKeys'

/** Swing `getData(action)`: parent≠table / TOP → addAll=false; putFullData → full; INLINE → selected row. */
export type TableLeafGetDataMode = 'external' | 'full' | 'inline'

const TABLE_LEAF_TYPES = new Set(['TABLE', 'EDITABLE', 'TREETABLE', 'GROUPTABLE'])

export type TableLeafFieldMeta = {
  fieldName: string
  elementType: string
  withCheckBoxes: boolean
  elementId?: string | number
  selectedRows?: readonly unknown[] | null
}

export type TableLeafGetDataResult =
  | { kind: 'omit' }
  | {
      kind: 'table'
      fieldName: string
      field: { type: 'UniDataTable'; value: Record<string, unknown> }
    }
  | { kind: 'inline'; fields: Record<string, unknown> }

function normalizeElementType(type: unknown): string {
  return String(type ?? '')
    .trim()
    .toUpperCase()
}

export function isTableLeafElementType(type: unknown): boolean {
  return TABLE_LEAF_TYPES.has(normalizeElementType(type))
}

export function readElementWithCheckBoxes(source: unknown): boolean {
  if (!source || typeof source !== 'object') return false
  const raw = (source as { withCheckBoxes?: unknown }).withCheckBoxes
  if (raw === true || raw === 1) return true
  if (typeof raw === 'string') {
    const t = raw.trim().toLowerCase()
    return t === 'true' || t === '1'
  }
  if (raw && typeof raw === 'object' && '_text' in raw) {
    const t = String((raw as { _text?: unknown })._text ?? '')
      .trim()
      .toLowerCase()
    return t === 'true' || t === '1'
  }
  return false
}

function cloneRow(row: unknown): Record<string, unknown> {
  try {
    return JSON.parse(JSON.stringify(row)) as Record<string, unknown>
  } catch {
    return row && typeof row === 'object' ? { ...(row as Record<string, unknown>) } : {}
  }
}

function unwrapUniDataTableValue(tableField: unknown): Record<string, unknown> {
  if (tableField == null || typeof tableField !== 'object') {
    return { rows: [] }
  }
  const field = tableField as { value?: unknown; rows?: unknown[] }
  const rawValue = 'value' in field ? field.value : field
  if (rawValue == null) return { rows: [] }
  if (Array.isArray(rawValue)) return { rows: rawValue }
  if (typeof rawValue === 'object') return { ...(rawValue as Record<string, unknown>) }
  return { rows: [] }
}

function asUniDataTableField(
  fieldName: string,
  tableField: unknown,
  rows: unknown[],
): TableLeafGetDataResult {
  const tableValue = unwrapUniDataTableValue(tableField)
  return {
    kind: 'table',
    fieldName,
    field: {
      type: 'UniDataTable',
      value: {
        ...tableValue,
        rows,
        empty: rows.length === 0,
      },
    },
  }
}

function getTreeChildRows(row: unknown): unknown[] {
  const fromItems = getFieldValueFromRow(row, IKey.ITEMS)
  const nested = extractUniDataTableRows(fromItems)
  if (nested.length > 0) return nested
  if (row && typeof row === 'object') {
    const rec = row as { _items?: unknown }
    if (Array.isArray(rec._items)) return rec._items
  }
  return []
}

function setTreeChildRows(row: Record<string, unknown>, childRows: unknown[]): void {
  if (!row.fields || typeof row.fields !== 'object') {
    row.fields = {}
  }
  const fields = row.fields as Record<string, unknown>
  fields[IKey.ITEMS] = {
    type: 'UniDataTable',
    value: {
      rows: childRows,
      columns: [],
      attributes: {},
      empty: childRows.length === 0,
    },
  }
}

function rowIsChecked(row: unknown, selectedRows: readonly unknown[]): boolean {
  if (!Array.isArray(selectedRows) || selectedRows.length === 0) return false
  return selectedRows.some((selected) => isSameTableSelectionRow(row, selected))
}

/**
 * Swing TreeTable getDataChildren(addAll=false): узел, если checked или есть checked потомок;
 * дети — UniDataTable в IKey.ITEMS.
 */
export function collectTreeTableCheckedRows(
  rows: readonly unknown[],
  selectedRows: readonly unknown[] | null | undefined,
): unknown[] {
  const selected = Array.isArray(selectedRows) ? selectedRows : []
  const out: unknown[] = []

  for (const raw of rows) {
    const children = getTreeChildRows(raw)
    const nested = collectTreeTableCheckedRows(children, selected)
    const selfChecked = rowIsChecked(raw, selected)
    if (!selfChecked && nested.length === 0) continue

    const clone = cloneRow(raw)
    if (children.length > 0) {
      setTreeChildRows(clone, nested)
    }
    out.push(clone)
  }

  return out
}

function flattenInlineRow(row: unknown): Record<string, unknown> {
  if (!row || typeof row !== 'object') return {}
  const rec = row as Record<string, unknown>
  if (rec.fields && typeof rec.fields === 'object' && !Array.isArray(rec.fields)) {
    return rec.fields as Record<string, unknown>
  }
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(rec)) {
    if (key === 'fields' || key.startsWith('_')) continue
    out[key] = value
  }
  return out
}

/**
 * Leaf getData одной таблицы (SwingTable / SwingTreeTable / EDITABLE).
 */
export function collectTableLeafGetData(input: {
  fieldName: string
  elementType: string
  withCheckBoxes: boolean
  mode: TableLeafGetDataMode
  tableField?: unknown
  selectedRows?: readonly unknown[] | null
}): TableLeafGetDataResult {
  const fieldName = String(input.fieldName ?? '').trim()
  if (!fieldName) return { kind: 'omit' }

  const type = normalizeElementType(input.elementType)
  const mode = input.mode
  const selected = input.selectedRows ?? []
  const allRows = extractUniDataTableRows(input.tableField)

  if (mode === 'inline') {
    const row = selected[0] ?? allRows[0]
    if (row == null) return { kind: 'omit' }
    return { kind: 'inline', fields: flattenInlineRow(row) }
  }

  // SwingTable.getData: isColumnService(0) before ET_EDIT_TABLE — checkbox wins over EdiTable.
  const isCheckboxTable =
    (type === 'TABLE' || type === 'EDITABLE' || type === 'GROUPTABLE') && input.withCheckBoxes
  if (isCheckboxTable) {
    const patch = buildCheckboxTableGetDataField({
      fieldName,
      tableField: input.tableField,
      selectedRows: selected,
      addAll: mode === 'full',
    })
    const field = patch[fieldName]
    if (!field) return { kind: 'omit' }
    return { kind: 'table', fieldName, field }
  }

  if (type === 'EDITABLE') {
    return asUniDataTableField(fieldName, input.tableField, allRows)
  }

  if (type === 'TREETABLE') {
    if (mode === 'full') {
      return asUniDataTableField(fieldName, input.tableField, allRows)
    }
    const checked = collectTreeTableCheckedRows(allRows, selected)
    return asUniDataTableField(fieldName, input.tableField, checked)
  }

  if (type === 'TABLE' || type === 'GROUPTABLE') {
    if (mode === 'full') {
      return asUniDataTableField(fieldName, input.tableField, allRows)
    }
    return { kind: 'omit' }
  }

  return { kind: 'omit' }
}

function pushTableLeafMetaFromRegistry(
  rec: Record<string, unknown>,
  wanted: string,
  seen: Set<string>,
  metas: TableLeafFieldMeta[],
): void {
  const getType = rec.getType
  const typeFromFn = typeof getType === 'function' ? getType() : undefined
  const type = normalizeElementType(rec.elementType ?? typeFromFn)
  if (!TABLE_LEAF_TYPES.has(type)) return
  if (wanted) {
    const ctx = rec.ctx as { formId?: unknown } | undefined
    const regFormId = String(rec.formId ?? ctx?.formId ?? '').trim()
    if (regFormId && regFormId !== wanted) return
  }
  const fieldName = String(rec.fieldName ?? rec.name ?? '').trim()
  if (!fieldName || seen.has(fieldName)) return
  seen.add(fieldName)
  metas.push({
    fieldName,
    elementType: type,
    withCheckBoxes: readElementWithCheckBoxes(rec),
    elementId: rec.elementId as string | number | undefined,
  })
}

export function resolveTableLeafMetasFromElements(
  elements: Record<string, unknown> | null | undefined,
  formId?: string | number | null,
  extraMaps?: Array<Record<string, unknown> | null | undefined>,
): TableLeafFieldMeta[] {
  const wanted = formId != null && formId !== '' ? String(formId) : ''
  const metas: TableLeafFieldMeta[] = []
  const seen = new Set<string>()
  const maps: Array<Record<string, unknown> | null | undefined> = [elements, ...(extraMaps ?? [])]

  for (const map of maps) {
    if (!map || typeof map !== 'object') continue
    for (const reg of Object.values(map)) {
      if (!reg || typeof reg !== 'object') continue
      pushTableLeafMetaFromRegistry(reg as Record<string, unknown>, wanted, seen, metas)
    }
  }
  return metas
}

export function mergeSelectionOnlyCheckboxMetas(
  metas: TableLeafFieldMeta[],
  selections: ReadonlyArray<{ fieldName: string; rows?: readonly unknown[] | null }> | null | undefined,
): TableLeafFieldMeta[] {
  const byField = new Map(metas.map((m) => [m.fieldName, { ...m }]))
  for (const sel of selections ?? []) {
    const fieldName = String(sel?.fieldName ?? '').trim()
    if (!fieldName) continue
    const existing = byField.get(fieldName)
    if (existing) {
      existing.selectedRows = sel.rows ?? []
      existing.withCheckBoxes = true
      continue
    }
    byField.set(fieldName, {
      fieldName,
      elementType: 'TABLE',
      withCheckBoxes: true,
      selectedRows: sel.rows ?? [],
    })
  }
  return Array.from(byField.values())
}

function findRegisteredTableLeafGetData(
  fieldName: string,
  maps: Array<Record<string, unknown> | null | undefined> | undefined,
): ((action?: unknown) => unknown) | undefined {
  if (!maps?.length) return undefined
  for (const map of maps) {
    if (!map || typeof map !== 'object') continue
    const byKey = map[fieldName]
    const candidates = [
      byKey,
      ...Object.values(map).filter((reg) => {
        if (!reg || typeof reg !== 'object') return false
        const rec = reg as Record<string, unknown>
        const name = String(rec.fieldName ?? rec.name ?? '').trim()
        return name === fieldName
      }),
    ]
    for (const reg of candidates) {
      if (!reg || typeof reg !== 'object') continue
      const getData = (reg as { getData?: unknown }).getData
      if (typeof getData === 'function') {
        return (getData as (action?: unknown) => unknown).bind(reg)
      }
    }
  }
  return undefined
}

function tableFieldFromGetDataHook(
  fieldName: string,
  hookResult: unknown,
): { type: 'UniDataTable'; value: Record<string, unknown> } | null {
  if (hookResult == null || typeof hookResult !== 'object' || Array.isArray(hookResult)) {
    return null
  }
  const rec = hookResult as Record<string, unknown>
  const field = rec[fieldName]
  if (!field || typeof field !== 'object' || Array.isArray(field)) return null
  const typed = field as { type?: string; value?: unknown }
  const type = String(typed.type ?? '').trim()
  if (type !== 'UniDataTable' && type !== 'UniDataTableFields') return null
  const value = unwrapUniDataTableValue(field)
  return { type: 'UniDataTable', value }
}

/**
 * Переписать table-like поля UniData по leaf getData.
 * Не трогает Tiles / FileAttachments / MultiSelect (нет meta).
 */
export function applyTableLeafGetDataToFields(
  fields: Record<string, unknown>,
  metas: readonly TableLeafFieldMeta[],
  options?: {
    mode?: TableLeafGetDataMode
    shouldRewriteField?: (meta: TableLeafFieldMeta) => boolean
    elements?: Record<string, unknown> | null
    extraMaps?: Array<Record<string, unknown> | null | undefined>
  },
): Record<string, unknown> {
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) {
    return fields
  }
  if (!metas.length) return fields

  const mode = options?.mode ?? 'external'
  const hookMaps: Array<Record<string, unknown> | null | undefined> = [
    options?.elements,
    ...(options?.extraMaps ?? []),
  ]
  let next: Record<string, unknown> | null = null

  for (const meta of metas) {
    const fieldName = String(meta.fieldName ?? '').trim()
    if (!fieldName) continue
    if (options?.shouldRewriteField && !options.shouldRewriteField(meta)) continue

    if (mode === 'external') {
      const getData = findRegisteredTableLeafGetData(fieldName, hookMaps)
      if (getData) {
        try {
          const hooked = tableFieldFromGetDataHook(fieldName, getData())
          if (hooked) {
            if (!next) next = { ...fields }
            next[fieldName] = hooked
            continue
          }
        } catch {
          /* fall through to collectTableLeafGetData */
        }
      }
    }

    const result = collectTableLeafGetData({
      fieldName,
      elementType: meta.elementType,
      withCheckBoxes: meta.withCheckBoxes,
      mode,
      tableField: (next ?? fields)[fieldName],
      selectedRows: meta.selectedRows,
    })

    if (result.kind === 'omit') {
      if (!Object.prototype.hasOwnProperty.call(next ?? fields, fieldName)) continue
      if (!next) next = { ...fields }
      delete next[fieldName]
      continue
    }
    if (result.kind === 'table') {
      if (!next) next = { ...fields }
      next[fieldName] = result.field
    }
  }

  return next ?? fields
}
